import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  KnownKioskBrandingDtoPoweredByTheme,
  KnownKioskBrandingLogoDtoType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { Colors } from '../../../../ui/theme/colors';
import { useTextStyles } from '../../../../ui/theme/text';
import { KioskThemeSettingsForm, ThemeZone } from './form';

const useStyles = makeStyles(() => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '32px'
  },
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    borderBottom: `1px solid ${Colors.lightGray}`,
    minHeight: '72px',
    padding: '16px 24px'
  },
  titleText: {
    display: 'flex',
    flexDirection: 'column'
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  screens: {
    padding: '20px 24px'
  }
}));

const DEFAULT_LOGO_URL = '/media/kiosk/groundfloor_logo.svg';

export interface KioskThemePreviewButtonLabels {
  showInternal: boolean;
  internalLabel: string;
  showExternal: boolean;
  externalLabel: string;
}

interface ZoneStyle {
  primary: string;
  onPrimary: string;
  onSecondary: string;
  footerText: string;
  bgFallback: string;
}

function deriveZoneStyle(form: Partial<KioskThemeSettingsForm>, zone: ThemeZone): ZoneStyle {
  const dark = zone === 'dark';
  return {
    primary: (dark ? form.colorsDarkPrimary : form.colorsLightPrimary) || (dark ? '#ffffff' : '#000000'),
    onPrimary: (dark ? form.colorsDarkOnPrimary : form.colorsLightOnPrimary) || (dark ? '#000000' : '#ffffff'),
    onSecondary:
      (dark ? form.colorsDarkOnSecondary : form.colorsLightOnSecondary) ||
      (dark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)'),
    footerText: dark ? '#ffffff' : '#000000',
    bgFallback: dark ? '#1a1a1a' : '#f0f0f0'
  };
}

function resolveLogoSrc(form: Partial<KioskThemeSettingsForm>): string | null {
  if (form.logoType === KnownKioskBrandingLogoDtoType.None) return null;
  if (form.logoType === KnownKioskBrandingLogoDtoType.Branded) return form.logoSrc || null;
  return DEFAULT_LOGO_URL;
}

function resolvePoweredByImageSrc(poweredByTheme: string | undefined, zone: ThemeZone): string | null {
  if (!poweredByTheme || poweredByTheme === KnownKioskBrandingDtoPoweredByTheme.Hidden) return null;
  const v = zone === 'dark' ? 'light' : 'dark';
  if (poweredByTheme === KnownKioskBrandingDtoPoweredByTheme.Mono)
    return `/media/kiosk/powered_by_groundfloor_mono_${v}.png`;
  if (poweredByTheme === KnownKioskBrandingDtoPoweredByTheme.LastkMono)
    return `/media/kiosk/powered_by_lastk_${v}_mono.svg`;
  if (poweredByTheme === KnownKioskBrandingDtoPoweredByTheme.Lastk) return `/media/kiosk/powered_by_lastk_${v}.svg`;
  return null;
}

function ScreenFooter({ zone, poweredByTheme }: { zone: ThemeZone; poweredByTheme: string | undefined }) {
  const src = resolvePoweredByImageSrc(poweredByTheme, zone);
  if (!src) return null;
  return (
    <Box
      style={{
        flexShrink: 0,
        height: '10%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <img src={src} alt="Powered by" style={{ maxHeight: 16, maxWidth: '70%', objectFit: 'contain' }} />
    </Box>
  );
}

function ScreenButtons({
  style,
  buttons
}: {
  style: ZoneStyle;
  buttons: { label: string; variant: 'primary' | 'secondary' }[];
}) {
  return (
    <Box style={{ display: 'flex', gap: 6, padding: '0 8% 6%' }}>
      {buttons.map((btn) => (
        <Box
          key={btn.label}
          style={{
            flex: 1,
            background: btn.variant === 'primary' ? style.primary : 'transparent',
            color: btn.variant === 'primary' ? style.onPrimary : style.onSecondary,
            border: btn.variant === 'secondary' ? `1px solid ${style.onSecondary}` : undefined,
            borderRadius: 18,
            padding: '8px 2px',
            textAlign: 'center',
            fontSize: 10,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: '20px'
          }}
        >
          {btn.label}
        </Box>
      ))}
    </Box>
  );
}

function HomeScreen({
  form,
  bodyStyle,
  footerZone,
  buttonLabels
}: {
  form: Partial<KioskThemeSettingsForm>;
  bodyStyle: ZoneStyle;
  footerZone: ThemeZone;
  buttonLabels: KioskThemePreviewButtonLabels;
}) {
  const logo = resolveLogoSrc(form);

  const buttons = [
    buttonLabels.showInternal ? { label: buttonLabels.internalLabel, variant: 'primary' as const } : null,
    buttonLabels.showExternal ? { label: buttonLabels.externalLabel, variant: 'primary' as const } : null
  ].filter((b): b is NonNullable<typeof b> => b !== null);

  return (
    <Box
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: form.backgroundHome ? `url(${form.backgroundHome}) center / cover no-repeat` : bodyStyle.bgFallback,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '50%',
          paddingLeft: '15%',
          paddingRight: '15%',
          minHeight: '25%'
        }}
      >
        {logo && (
          <img
            src={logo}
            alt="Logo"
            style={{ maxWidth: '100%', maxHeight: 72, objectFit: 'contain', display: 'block' }}
          />
        )}
      </Box>

      <Box style={{ flex: 1 }} />

      {buttons.length > 0 && <ScreenButtons style={bodyStyle} buttons={buttons} />}

      <ScreenFooter zone={footerZone} poweredByTheme={form.poweredByTheme} />
    </Box>
  );
}

function SessionScreen({
  form,
  bodyStyle,
  footerZone,
  bgFallback
}: {
  form: Partial<KioskThemeSettingsForm>;
  bodyStyle: ZoneStyle;
  footerZone: ThemeZone;
  bgFallback: string;
}) {
  const logo = resolveLogoSrc(form);
  const buttons = [
    { label: 'Primary', variant: 'primary' as const },
    { label: 'Secondary', variant: 'secondary' as const }
  ];

  return (
    <Box
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: form.backgroundSession ? `url(${form.backgroundSession}) center / cover no-repeat` : bgFallback,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '4%' }}>
        {logo && (
          <img
            src={logo}
            alt="Logo"
            style={{ maxWidth: '60%', maxHeight: 24, objectFit: 'contain', display: 'block' }}
          />
        )}
      </Box>
      <Box style={{ flex: 1 }} />
      <ScreenButtons style={bodyStyle} buttons={buttons} />
      <ScreenFooter zone={footerZone} poweredByTheme={form.poweredByTheme} />
    </Box>
  );
}

function DeviceFrame({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box style={{ flex: 1, textAlign: 'center' }}>
      <Box
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 300,
          margin: '0 auto',
          aspectRatio: '9 / 16',
          border: '3px solid #cccccc',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#e0e0e0'
        }}
      >
        {children}
      </Box>
      <Typography variant="caption" style={{ color: '#888', display: 'block', marginTop: 6 }}>
        {label}
      </Typography>
    </Box>
  );
}

export function KioskThemePreview({
  formData,
  buttonLabels,
  title
}: {
  formData: Record<string, any>;
  buttonLabels: KioskThemePreviewButtonLabels;
  title?: string;
}) {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const form = formData as Partial<KioskThemeSettingsForm>;

  if (!form.themeHome) return null;

  const homeZone = form.themeHome ?? 'light';
  const homeFooterZone = form.themeHomeFooter ?? 'light';
  const sessionZone = form.themeOtherScreens ?? 'light';
  const sessionFooterZone = form.themeSessionFooter ?? 'light';

  const homeBodyStyle = deriveZoneStyle(form, homeZone);
  const sessionBodyStyle = deriveZoneStyle(form, sessionZone);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.title}>
        <div className={classes.titleText}>
          <div className={classes.titleRow}>
            <Typography className={textClasses.h2}>{title ?? 'Theme'}</Typography>
          </div>
        </div>
        <Typography variant="h6" style={{ color: Colors.collectedGreen }}>
          Preview
        </Typography>
      </Box>
      <Box
        className={classes.screens}
        style={{ gap: 16 }}
        sx={{
          display: 'flex'
        }}
      >
        <DeviceFrame label="Home">
          <HomeScreen form={form} bodyStyle={homeBodyStyle} footerZone={homeFooterZone} buttonLabels={buttonLabels} />
        </DeviceFrame>
        <DeviceFrame label="Session">
          <SessionScreen
            form={form}
            bodyStyle={sessionBodyStyle}
            footerZone={sessionFooterZone}
            bgFallback={sessionBodyStyle.bgFallback}
          />
        </DeviceFrame>
      </Box>
    </Paper>
  );
}
